'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { usePathname } from 'next/navigation';
import { cn } from '@utils/cn';
import type { PromptBarMode } from './usePromptBarMode';
import { type ActionItem, usePromptBarActions } from './usePromptBarActions';

interface MissionChipsRowProps {
  locale: Locale;
  mode: PromptBarMode;
  isAuthenticated: boolean;
  className?: string;
}

const chipClassName = (selected: boolean): string =>
  cn(
    'rounded-full border border-input bg-surface px-2.5 py-1 text-xs text-ink shadow-sm transition-colors lg:text-sm',
    selected ? 'font-semibold' : 'cursor-pointer hover:bg-surface-100',
  );

/**
 * The mission list rendered as a centered row of chips below the prompt bar. The current
 * mission's chip looks identical to the rest but clicking it does nothing (handleClick
 * no-ops on the selected item).
 *
 * The Hire chip is not a navigation chip — it is a menu trigger. Clicking it opens a small
 * popover anchored directly above it containing the company chips (Add job, My Jobs), so it
 * is clear they belong to Hire. A popover chip is bold + inactive when we are on its page.
 * The popover closes on outside-click, Esc, or navigation (pathname change). Wraps onto
 * multiple rows on narrow viewports.
 */
export const MissionChipsRow = ({ locale, mode, isAuthenticated, className }: MissionChipsRowProps) => {
  const { t } = useTranslation('common');
  const pathname = usePathname();
  const { candidateItems, companyItems, handleClick } = usePromptBarActions(locale, mode, isAuthenticated);
  const itemsByKey = new Map([...candidateItems, ...companyItems].map((item) => [item.key, item]));

  const [hireOpen, setHireOpen] = useState(false);
  const hireRef = useRef<HTMLDivElement>(null);

  // Close on navigation — the popover is ephemeral and should not survive a page change.
  useEffect(() => { setHireOpen(false); }, [pathname]);

  // Close on outside-click / Esc while open.
  useEffect(() => {
    if (!hireOpen) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (hireRef.current && !hireRef.current.contains(event.target as Node)) setHireOpen(false);
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setHireOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [hireOpen]);

  // Row order: Interviews, Jobs, Hire (popover), Coach, Profile.
  const leadingChips = [
    { item: itemsByKey.get('interviews'), label: t('interviews.menu.title') },
    { item: itemsByKey.get('jobs'), label: t('mc.chip.jobs') },
  ].filter((entry): entry is { item: ActionItem; label: string } => Boolean(entry.item));
  const trailingChips = [
    { item: itemsByKey.get('coaching'), label: t('mc.chip.coach') },
    { item: itemsByKey.get('edit-profile'), label: t('mc.chip.profile') },
  ].filter((entry): entry is { item: ActionItem; label: string } => Boolean(entry.item));

  const hireChips = [
    { item: itemsByKey.get('add-job'), label: t('mc.chip.add') },
    { item: itemsByKey.get('candidate-management'), label: t('header.label.jobs.v2') },
  ].filter((entry): entry is { item: ActionItem; label: string } => Boolean(entry.item));

  const renderNavChip = ({ item, label }: { item: ActionItem; label: string }, extra?: string) => (
    <a
      key={item.key}
      href={item.href}
      aria-current={item.selected ? 'page' : undefined}
      onClick={(event) => {
        // Let the browser handle modifier / middle clicks (open in new tab); intercept
        // plain left-clicks for soft-navigation through handleClick.
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        handleClick(item);
      }}
      className={cn(chipClassName(item.selected), extra)}
    >
      {label}
    </a>
  );

  return (
    <div className={cn('flex w-full flex-wrap items-center justify-center gap-1.5', className)}>
      {leadingChips.map((entry) => renderNavChip(entry))}

      {hireChips.length > 0 && (
        <div ref={hireRef} className="relative">
          <button
            type="button"
            aria-expanded={hireOpen}
            aria-haspopup="menu"
            onClick={() => setHireOpen((open) => !open)}
            className={chipClassName(hireOpen)}
          >
            {t('hire.menu')}
            <span className={cn('ml-1 inline-block text-[0.65em] transition-transform', hireOpen && 'rotate-180')}>▾</span>
          </button>

          {hireOpen && (
            <div
              role="menu"
              className="absolute bottom-full left-1/2 mb-2 flex -translate-x-1/2 flex-col gap-1.5 rounded-2xl bg-surface p-1.5 shadow-lg"
            >
              {hireChips.map((entry) => renderNavChip(entry, 'whitespace-nowrap shadow-none'))}
              <span className="absolute left-1/2 top-full size-2 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-surface" />
            </div>
          )}
        </div>
      )}

      {trailingChips.map((entry) => renderNavChip(entry))}
    </div>
  );
};
